// ============================================================
// DiceTray - free-form dice roller. Click die buttons to build
// a pool ("2d10 + 1d5 + 2"), adjust a flat modifier, and Roll;
// or type any expression the dice engine parses ("3d10+4",
// "d100", "2d10-3"). Results go to the narrative logs via the
// parent's roll handler; the last result also shows inline.
// ============================================================
import { memo, useState } from 'react';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/ActionSheet.module.css';
import { formatDiceResult } from '../engine/structuredNarrative';
import type { DiceResult } from '../types';

interface DiceTrayProps {
  onRollExpr: (expr: string) => DiceResult | null;
}

const DIE_SIDES = [3, 5, 6, 10, 100];

interface LastRoll {
  expr: string;
  result: DiceResult;
}

/** The sides in the pool, largest first. */
function poolSides(pool: Record<number, number>): number[] {
  return DIE_SIDES.filter((s) => pool[s] > 0).sort((a, b) => b - a);
}

/** The pool as the dice engine reads it: "2d10+1d5+2". */
function buildPoolExpr(pool: Record<number, number>, flatMod: number): string {
  let expr = poolSides(pool)
    .map((s) => `${pool[s]}d${s}`)
    .join('+');
  if (flatMod > 0) expr += (expr ? '+' : '') + flatMod;
  else if (flatMod < 0) expr += String(flatMod);
  return expr;
}

function DiceTray({ onRollExpr }: DiceTrayProps) {
  const [pool, setPool] = useState<Record<number, number>>({});
  const [flatMod, setFlatMod] = useState(0);
  const [lastRoll, setLastRoll] = useState<LastRoll | null>(null);
  const poolExpr = buildPoolExpr(pool, flatMod);

  const addDie = (sides: number) => setPool((p) => ({ ...p, [sides]: (p[sides] || 0) + 1 }));
  const removeDie = (sides: number) =>
    setPool((p) => {
      const next = { ...p, [sides]: Math.max(0, (p[sides] || 0) - 1) };
      if (next[sides] === 0) delete next[sides];
      return next;
    });
  /** Roll through the parent (which logs it); true when the engine could parse it. */
  const rollExpr = (expr: string): boolean => {
    const result = onRollExpr(expr);
    if (!result) return false;
    setLastRoll({ expr, result });
    return true;
  };

  return (
    <div data-testid="dice-tray">
      {/* Die buttons + flat modifier */}
      <div className={styles.trayRow}>
        {DIE_SIDES.map((sides) => (
          <button
            key={sides}
            type="button"
            className={styles.dieBtn}
            aria-label={`Add d${sides}`}
            title={`Add a d${sides} to the pool`}
            onClick={() => addDie(sides)}
          >
            d{sides}
          </button>
        ))}
        <FlatModStepper value={flatMod} onChange={setFlatMod} />
      </div>
      <PoolRow
        pool={pool}
        flatMod={flatMod}
        poolExpr={poolExpr}
        onRemove={removeDie}
        onRoll={() => poolExpr && rollExpr(poolExpr)}
        onClear={() => {
          setPool({});
          setFlatMod(0);
        }}
      />
      <TypedRoll rollExpr={rollExpr} />
      {lastRoll && (
        <div className={styles.lastResult} data-testid="tray-last-result">
          🎲 {lastRoll.expr} = <span className={styles.lastResultTotal}>{lastRoll.result.total}</span>{' '}
          <span>({formatDiceResult(lastRoll.result)})</span>
        </div>
      )}
    </div>
  );
}

function FlatModStepper({ value, onChange }: { value: number; onChange: (fn: (m: number) => number) => void }) {
  return (
    <span className={styles.modStepper}>
      <button
        type="button"
        className={styles.dieBtn}
        aria-label="Decrease modifier"
        onClick={() => onChange((m) => m - 1)}
      >
        −
      </button>
      <span data-testid="tray-flat-mod">{value >= 0 ? `+${value}` : value}</span>
      <button
        type="button"
        className={styles.dieBtn}
        aria-label="Increase modifier"
        onClick={() => onChange((m) => m + 1)}
      >
        +
      </button>
    </span>
  );
}

interface PoolRowProps {
  pool: Record<number, number>;
  flatMod: number;
  poolExpr: string;
  onRemove: (sides: number) => void;
  onRoll: () => void;
  onClear: () => void;
}

/** The pool as chips (click one to drop a die), then Roll and Clear. */
function PoolRow({ pool, flatMod, poolExpr, onRemove, onRoll, onClear }: PoolRowProps) {
  return (
    <div className={styles.trayRow}>
      {poolExpr ? (
        <>
          {poolSides(pool).map((s) => (
            <button
              key={s}
              type="button"
              className={styles.poolChip}
              aria-label={`Remove one d${s}`}
              title="Click to remove one"
              onClick={() => onRemove(s)}
            >
              {pool[s]}d{s}
            </button>
          ))}
          {flatMod !== 0 && <span className={styles.poolEmpty}>{flatMod > 0 ? `+${flatMod}` : flatMod}</span>}
        </>
      ) : (
        <span className={styles.poolEmpty}>Click dice to build a pool</span>
      )}
      <span className={styles.trayActions}>
        <button type="button" className={common.btnSmall} disabled={!poolExpr} onClick={onRoll}>
          Roll
        </button>
        <button type="button" className={common.btnSmall} disabled={!poolExpr && flatMod === 0} onClick={onClear}>
          Clear
        </button>
      </span>
    </div>
  );
}

/** Any expression the dice engine parses; one it cannot is kept, with a hint. */
function TypedRoll({ rollExpr }: { rollExpr: (expr: string) => boolean }) {
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState(false);
  const rollTyped = () => {
    const expr = typed.trim();
    if (!expr) return;
    const ok = rollExpr(expr);
    setTypedError(!ok);
    if (ok) setTyped('');
  };
  return (
    <>
      <div className={styles.trayRow}>
        <input
          className={clsx(common.input, styles.exprInput)}
          placeholder="or type: 3d10+4"
          aria-label="Dice expression"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setTypedError(false);
          }}
          onKeyDown={(e) => e.key === 'Enter' && rollTyped()}
        />
        <button type="button" className={common.btnSmall} disabled={!typed.trim()} onClick={rollTyped}>
          Roll
        </button>
      </div>
      {typedError && <div className={styles.exprError}>Cannot parse that expression - try "2d10+3".</div>}
    </>
  );
}

export default memo(DiceTray);
